import { Button } from '@shared/components/common/Button';
import { cn } from '@shared/utils/utils';
import { ArrowLeft, CalendarDays, ChevronDown, FileDown, RefreshCcwIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { flowResult } from 'mobx';
import { CustomerInfo, PaymentInfo, ProductItem } from './components';
import { OrderDetail } from '@shared/types/order';
import { ORDER_STATUS_COLORS, ORDER_STATUS_LABELS } from '@shared/components/constants/select.constant';
import { useStore } from '@shared/hooks/useStore';
import { ORDER_STATUS } from '@shared/types/enums';
import { useRepayment } from '@shared/hooks/useRepayment';
import { formatDateTime } from '@/shared/utils/datetime';
import { DATE_TIME_YMD } from '@/shared/components/constants/common.constants';

export default observer(function PurchaseHistoryDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { orderStore, apiStore, modalStore } = useStore();
    const handleRepayment = useRepayment();
    const [showAllProducts, setShowAllProducts] = useState(false);
    const [order, setOrder] = useState<OrderDetail | null>(null);

    const isLoading = apiStore.isLoading;

    useEffect(() => {
        if (id) {
            const fetchOrderDetail = async () => {
                const orderDetail = await flowResult(orderStore.getOrderDetail(id));
                if (orderDetail) {
                    setOrder(orderDetail);
                }
            };
            fetchOrderDetail();
        }
    }, [id, orderStore]);

    const handleBack = () => {
        navigate(-1);
    };

    const onRepayment = () => {
        if (order?.id) {
            handleRepayment(order.id);
        }
    };

    const handleExportInvoice = async () => {
        if (!order?.id) return;

        try {
            const result = await flowResult(orderStore.exportInvoicePDF(order.id));
            if (!result?.success) {
                modalStore.showAlertModal({
                    id: 'export-invoice-error',
                    type: 'error',
                    size: 'alert',
                    content: '支払明細書のダウンロードに失敗しました。',
                    cancelButton: false,
                    saveButton: '閉じる'
                });
            }
        } catch (error) {
            console.error('Error exporting invoice:', error);
        }
    };

    if (isLoading || !order) {
        return (
            <section className='space-y-4 w-full'>
                <div className='bg-white rounded-lg shadow p-6 text-center'>
                    <p className='text-gray-600'>読み込み中...</p>
                </div>
            </section>
        );
    }

    const products = order.orderDetails;
    const hasMoreProducts = products.length > 2;

    return (
        <section className='space-y-4 w-full animate-in fade-in slide-in-from-right-40 duration-300'>
            {/* Header with Back Button */}
            <div className='flex items-center -ml-3'>
                <Button
                    variant='ghost'
                    onClick={handleBack}
                    size='icon'
                >
                    <ArrowLeft size={20} />
                </Button>
                <div className='flex items-center gap-2.5 flex-1'>
                    <span className='text-[#009ACD] font-bold text-base md:text-xl'>{order.code}</span>
                    <span className={cn('px-3 py-1 rounded-full text-sm font-semibold', ORDER_STATUS_COLORS[order.status])}>
                        {ORDER_STATUS_LABELS[order.status]}
                    </span>
                </div>
            </div>

            {/* Order Date */}
            <div className='text-sm text-text-secondary'>
                <CalendarDays size={20} className='inline-flex mb-0.5' />
                {' '}
                <span className='text-text-secondary'>注文日</span>
                {' '}
                <span className='font-medium'>
                    {formatDateTime(order.orderAt, DATE_TIME_YMD)}
                </span>
            </div>

            {/* Product Details Section */}
            <div className='bg-white rounded-lg shadow p-4 md:p-6'>
                <h3 className='font-medium mb-3'>商品名</h3>
                <ul className='space-y-3'>
                    {/* Always show first 2 products */}
                    {products.slice(0, 2).map(product => (
                        <li key={product.id}>
                            <ProductItem product={product} />
                        </li>
                    ))}
                </ul>
                {/* Animated expandable section for remaining products */}
                {hasMoreProducts && (
                    <>
                        <div
                            className='grid transition-all duration-300 ease-in-out'
                            style={{
                                gridTemplateRows: showAllProducts ? '1fr' : '0fr'
                            }}
                        >
                            <ul className='space-y-3 overflow-hidden'>
                                {products.slice(2).map(product => (
                                    <li
                                        key={product.id}
                                        className={cn(
                                            'transition-all duration-300',
                                            showAllProducts ? 'opacity-100 translate-y-0 pt-3' : 'opacity-0 -translate-y-2'
                                        )}
                                    >
                                        <ProductItem product={product} />
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <Button
                            variant='ghost'
                            onClick={() => setShowAllProducts(!showAllProducts)}
                            className='w-full bg-accent mt-3 min-h-[42px] gap-2'
                        >
                            {showAllProducts ? '折りたたむ' : 'もっと見る'}
                            <ChevronDown size={16} className={cn('transition-transform duration-300', showAllProducts ? 'rotate-180' : '')} />
                        </Button>
                    </>
                )}
                <hr className='my-3' />
                {order.user && (
                    <CustomerInfo
                        customer={{
                            furigana: `${order.user.first_name_furi || ''} ${order.user.last_name_furi || ''}`.trim(),
                            email: order.user.email || '',
                            phone: order.user.phone_number || ''
                        }}
                    />
                )}
            </div>

            {/* Payment Information Section */}
            <PaymentInfo order={order} />
            <div className='flex justify-center'>
                {order.status === ORDER_STATUS.PAID ?
                    (
                        <Button
                            onClick={handleExportInvoice}
                            className='min-h-14 min-w-[270px] mx-auto text-base gap-2'
                            size='lg'
                        >
                            <FileDown size={18} />
                            支払明細書
                        </Button>
                    ) :
                    (
                        <Button
                            onClick={onRepayment}
                            className='min-h-14 min-w-[270px] mx-auto text-base gap-2 bg-[#FFB145] hover:bg-[#FFB145]/90'
                            size='lg'
                        >
                            <RefreshCcwIcon size={18} />
                            再支払
                        </Button>
                    )}
            </div>

        </section>
    );
});
